import { faCircleInfo } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, PinInput } from '@realtyka/real-ui';
import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import AuthPageLayoutV2 from '../../components/Layout/AuthPageLayoutV2';
import { useLogout } from '../../hooks/useLogout';
import useQueryParams from '../../hooks/useQueryParams';
import {
  JwtAuthenticationResponseMfaTypeEnum,
  MfaControllerApi,
} from '../../openapi/keymaker';
import ErrorService from '../../services/ErrorService';
import { fetchAuthUserDetail } from '../../slices/AuthSlice';
import { useAppDispatch } from '../../slices/store';
import { RootState } from '../../types';
import { getAuthCookie, setAuthCookie } from '../../utils/AuthUtils';
import { getCommonsApiErrorMessage } from '../../utils/ErrorUtils';
import {
  handleSessionExpiration,
  handleUnauthorizedOnly,
} from '../../utils/MfaErrorHandlingUtils';
import { getKeymakerConfiguration } from '../../utils/OpenapiConfigurationUtils';
import { maskPhoneNumberWithCountryCode } from '../../utils/StringUtils';
import { constructLink } from '../../utils/UrlUtils';

interface SignInMFARouteV2Props {}

interface Query {
  leadId?: string;
  redirectTo?: string;
}

const SignInMFARouteV2: React.FC<SignInMFARouteV2Props> = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const logout = useLogout();
  const isAgentContext = location.pathname === '/agent-login/mfa';
  const { leadId, redirectTo } = useQueryParams<Query>();
  const [isResendLoading, setIsResendLoading] = useState<boolean>(false);
  const [enableResendCode, setEnableResendCode] = useState<boolean>(true);
  const hiddenInputRef = useRef<HTMLInputElement>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const { jwtAuthResponseCurrentUser } = useSelector((state: RootState) => ({
    jwtAuthResponseCurrentUser: state.auth.jwtAuthResponseCurrentUser,
  }));

  const isSmsTypeMFA =
    jwtAuthResponseCurrentUser?.mfaType ===
    JwtAuthenticationResponseMfaTypeEnum.Sms;

  const onResendCode = async () => {
    setIsResendLoading(true);
    try {
      setEnableResendCode(false);
      setTimeout(() => {
        setEnableResendCode(true);
      }, 10000);
      await new MfaControllerApi(await getKeymakerConfiguration()).sendMfaSms();
    } catch (e: any) {
      const handled = await handleSessionExpiration({
        error: e,
        dispatch,
        navigate,
        isAgentContext,
        redirectTo,
      });

      if (handled) {
        return;
      }

      ErrorService.notifyIgnoreAuthErrors(
        'Unable to resend authentication code to phone number during 2fa sign in',
        e,
      );
    } finally {
      setIsResendLoading(false);
    }
  };

  const submitCode = async (code: string) => {
    setIsLoading(true);
    setErrorMessage('');
    try {
      const config = await getKeymakerConfiguration();
      const { data } = await new MfaControllerApi(config).signInWithMfa({
        code: parseInt(code, 10),
      });

      if (data?.accessToken) {
        setAuthCookie(data.accessToken);
        const { isLoginSuccess } = await dispatch(
          fetchAuthUserDetail(leadId || ''),
        );
        if (!isLoginSuccess) {
          setErrorMessage(
            'Looks like the account you are trying to login as is an agent account. Please login to the ReZEN app instead.',
          );
        }
      } else {
        setErrorMessage('No access token received from server.');
      }
    } catch (e: any) {
      const handled = await handleUnauthorizedOnly({
        error: e,
        dispatch,
        navigate,
        isAgentContext,
        redirectTo,
      });

      if (handled) {
        return;
      }
      setErrorMessage(getCommonsApiErrorMessage(e));
      ErrorService.notifyIgnoreAuthErrors(
        'Unable to sign in with two-factor authentication',
        e,
        {
          data: { code },
        },
      );
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!getAuthCookie()) {
      navigate(
        constructLink(isAgentContext ? '/agent-login' : '/login', {
          redirectTo,
        }),
      );
    }
  }, [navigate, redirectTo, isAgentContext]);

  return (
    <AuthPageLayoutV2>
      <div className='flex flex-col justify-center items-center w-full h-full'>
        <div className='flex flex-col items-center w-[340px] md:w-[524px] py-10 px-4 md:px-10 gap-y-6 border border-reskin-grey-300 rounded-xl bg-white shadow-sm'>
          <div className='flex flex-col items-start gap-y-2 self-stretch'>
            <div className='text-reskin-primary-dark text-center font-poppins-semibold text-[28px] md:text-[32px] leading-10 tracking-[-0.5px]'>
              Two-Factor Authentication
            </div>
            {!!isSmsTypeMFA ? (
              <div className='text-reskin-primary-dark text-center text-sm md:text-base font-inter-regular'>
                Two-factor authentication is enabled for your account. Please
                enter the 6-digit code sent to{' '}
                <span className='font-inter-medium'>
                  {maskPhoneNumberWithCountryCode(
                    jwtAuthResponseCurrentUser?.phoneNumber!,
                  ) || 'N/A'}
                </span>
              </div>
            ) : (
              <div className='text-reskin-primary-dark text-center text-sm md:text-base font-inter-regular'>
                Two-factor authentication is enabled for your account. Enter the
                code from the authenticator app on your mobile device.
              </div>
            )}
          </div>

          <div className='flex flex-col justify-center self-stretch'>
            <PinInput
              onComplete={(code) => {
                hiddenInputRef.current?.focus();
                submitCode(code);
              }}
              error={errorMessage}
              data-testid='pin-input'
              disabled={isLoading}
            />
          </div>

          {errorMessage && (
            <div className='flex py-3 px-4 justify-center items-center gap-2 self-stretch rounded-[10px] bg-reskin-red-100'>
              <FontAwesomeIcon
                icon={faCircleInfo}
                className='text-reskin-red-600 text-sm md:text-base'
              />
              <div className='text-reskin-red-600 font-inter-medium text-sm md:text-base'>
                {errorMessage}
              </div>
            </div>
          )}

          <div className='flex flex-col gap-y-2 w-full'>
            {!!isSmsTypeMFA && (
              <Button
                size='sm'
                type='button'
                variant='outline'
                disabled={isResendLoading || !enableResendCode}
                loading={isResendLoading}
                onClick={onResendCode}
                color='secondary'
              >
                Resend Code
              </Button>
            )}
            <Button
              variant='outline'
              size='sm'
              type='button'
              fullWidth
              color='secondary'
              onClick={async () => {
                await logout();
                navigate(
                  constructLink(isAgentContext ? '/agent-login' : '/login', {
                    redirectTo,
                  }),
                );
              }}
            >
              Cancel
            </Button>
          </div>
        </div>
      </div>
    </AuthPageLayoutV2>
  );
};

export default SignInMFARouteV2;
